import { useEffect, useState } from "react";
import { InstallAvailabilityCalendar } from "@/components/availability/InstallAvailabilityCalendar";
import { useQuoteInstallDates, useSetQuoteAvailability } from "@/api/hooks";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatDateOnlyLabel } from "@/utils/dateOnly";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

type Props = {
  quoteId: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: (date: string) => void;
};

export default function CustomerAvailabilityModal({ quoteId, open, onOpenChange, onSaved }: Props) {
  const enabled = open && !!quoteId && quoteId > 0;
  const { data, isLoading, isError } = useQuoteInstallDates(quoteId, enabled);
  const save = useSetQuoteAvailability();
  const [selected, setSelected] = useState<string | null>(null);
  const savedDate = data?.selected ?? null;
  const isChanging = Boolean(savedDate);

  useEffect(() => {
    if (!open) return;
    setSelected(savedDate);
  }, [open, savedDate]);

  const handleConfirm = async () => {
    if (!quoteId || !selected) return;
    if (selected === savedDate) {
      onOpenChange(false);
      return;
    }
    try {
      await save.mutateAsync({ quoteId, date: selected });
      toast.success(isChanging ? "Installation date updated." : "Installation date saved.");
      onSaved?.(selected);
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not save that date.");
    }
  };

  const earliestLabel = formatDateOnlyLabel(data?.earliestSelectable);
  const dates = data?.dates ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0d1b2e] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">
            {isChanging ? "Change your installation date" : "When can installation take place?"}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-white/60">
          {isChanging
            ? `You're currently available on ${formatDateOnlyLabel(savedDate)}. Pick a different day — you can change this until you sign.`
            : "Choose a day you will be available."}{" "}
          Dates start two weeks after your proposal
          {earliestLabel ? ` (${earliestLabel})` : ""}.
        </p>
        {isLoading ? (
          <div className="flex items-center gap-2 justify-center py-10">
            <Loader2 className="h-6 w-6 text-[#29ffa9] animate-spin" />
          </div>
        ) : isError ? (
          <p className="text-sm text-red-300">Could not load available dates. Please try again.</p>
        ) : dates.length === 0 ? (
          <p className="text-sm text-white/50 py-6">
            No installation dates are available yet. We&apos;ll be in touch once installers in your area have published availability.
          </p>
        ) : (
          <div className="flex justify-center">
            <InstallAvailabilityCalendar
              mode="single"
              selected={selected}
              onSelect={setSelected}
              allowedDates={dates}
            />
          </div>
        )}
        <Button
          className="btn-cta w-full"
          disabled={!selected || save.isPending || dates.length === 0}
          onClick={() => void handleConfirm()}
        >
          {save.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          {isChanging ? "Save new date" : "Confirm date"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
